import { Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';

const PRIORITY_LABEL = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

const SORT_FIELD_OPTIONS = [
  { value: 'created_at', label: 'Created date' },
  { value: 'due_date', label: 'Due date' },
];

const SORT_DIRECTION_OPTIONS = [
  { value: 'asc', label: 'Oldest first' },
  { value: 'desc', label: 'Newest first' },
];

function TaskTable({
  tasks,
  loading,
  statusOptions,
  sortConfig,
  onEdit,
  onStatusChange,
  onToggleComplete,
  onDelete,
  onSortChange,
}) {
  return (
    <Card variant="outlined">
      <CardHeader
        title="Table view"
        subheader="Perfect for quick edits and reporting."
        action={
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ minWidth: { md: 380 } }}>
            <TextField
              select
              size="small"
              label="Sort by"
              value={sortConfig.field}
              onChange={(event) => onSortChange(event.target.value, sortConfig.direction)}
            >
              {SORT_FIELD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Order"
              value={sortConfig.direction}
              onChange={(event) => onSortChange(sortConfig.field, event.target.value)}
            >
              {SORT_DIRECTION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        }
      />
      <CardContent>
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : tasks.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No tasks found. Try adjusting your filters or create a new task.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Complete</TableCell>
                  <TableCell>Task</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Assignee</TableCell>
                  <TableCell>Due date</TableCell>
                  <TableCell>Created date</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task) => (
                  <Fragment key={task.id}>
                    <TableRow hover key={`parent-${task.id}`}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={Boolean(task.is_completed)}
                          onChange={(event) => onToggleComplete(task, event.target.checked)}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {task.title}
                        </Typography>
                        {task.notes && (
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {task.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{task.client_name || '—'}</TableCell>
                      <TableCell>{task.assignee_name || '—'}</TableCell>
                      <TableCell>
                        {task.due_date ? dayjs(task.due_date).format('MMM D, YYYY') : '—'}
                      </TableCell>
                      <TableCell>
                        {task.created_at ? dayjs(task.created_at).format('MMM D, YYYY') : '—'}
                      </TableCell>
                      <TableCell>{PRIORITY_LABEL[task.priority] || task.priority_display || '—'}</TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          value={task.status || 'backlog'}
                          onChange={(event) => onStatusChange(task, event.target.value)}
                        >
                          {statusOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit task">
                          <IconButton size="small" onClick={() => onEdit(task)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete task">
                          <IconButton
                            size="small"
                            onClick={() => onDelete(task)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    {(task.subtasks || []).map((subtask) => (
                      <TableRow key={`${task.id}-${subtask.id}`} sx={{ backgroundColor: (theme) => theme.palette.action.hover }}>
                        <TableCell padding="checkbox" sx={{ pl: 4 }}>
                          <Checkbox
                            checked={Boolean(subtask.is_completed)}
                            onChange={(event) => onToggleComplete(subtask, event.target.checked)}
                          />
                        </TableCell>
                        <TableCell sx={{ pl: 4 }}>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {subtask.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Subtask
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{task.client_name || '—'}</TableCell>
                        <TableCell>{subtask.assignee_name || '—'}</TableCell>
                        <TableCell>
                          {subtask.due_date ? dayjs(subtask.due_date).format('MMM D, YYYY') : '—'}
                        </TableCell>
                        <TableCell>
                          {subtask.created_at ? dayjs(subtask.created_at).format('MMM D, YYYY') : '—'}
                        </TableCell>
                        <TableCell>{PRIORITY_LABEL[subtask.priority] || subtask.priority_display || '—'}</TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={subtask.status || 'backlog'}
                            onChange={(event) => onStatusChange(subtask, event.target.value)}
                          >
                            {statusOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit subtask">
                            <IconButton size="small" onClick={() => onEdit(subtask)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete subtask">
                            <IconButton
                              size="small"
                              onClick={() => onDelete(subtask)}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

TaskTable.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      subtasks: PropTypes.arrayOf(PropTypes.object),
    }),
  ),
  loading: PropTypes.bool,
  statusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  sortConfig: PropTypes.shape({
    field: PropTypes.oneOf(['created_at', 'due_date']).isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onToggleComplete: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
};

TaskTable.defaultProps = {
  tasks: [],
  loading: false,
};

export default TaskTable;
